"use client";

import React, { useEffect, useRef, useState } from "react";
import { Mail, Send, Check, Headphones, MessageSquare, Sparkles } from "lucide-react";
import Link from "next/link";
import {
  Input,
  Textarea,
  Select,
  Button,
  Container,
  PageHeroLayout,
} from "../components/common";
import { submitContactForm } from "../services/contactService";
import { getVisualCmsIcon } from "@/lib/visual-cms/icon-registry";
import RecaptchaField, { type RecaptchaFieldRef } from "@/components/RecaptchaField";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const subjectOptions = [
  { value: "general", label: "General Enquiry" },
  { value: "booking", label: "Booking Issue" },
  { value: "listing", label: "Listing Help" },
  { value: "payment", label: "Payment Issue" },
  { value: "dispute", label: "Dispute or Report" },
  { value: "trust-safety", label: "Trust & Safety" },
  { value: "account", label: "Account Help" },
  { value: "other", label: "Other" },
];

const validate = (data: FormData): FormErrors => {
  const errors: FormErrors = {};
  if (!data.firstName.trim()) errors.firstName = "First name is required";
  if (!data.lastName.trim()) errors.lastName = "Last name is required";
  if (!data.email.trim()) errors.email = "Email is required";
  else if (!/\S+@\S+\.\S+/.test(data.email)) errors.email = "Please enter a valid email";
  if (!data.subject) errors.subject = "Please select a topic";
  if (!data.message.trim()) errors.message = "Message is required";
  else if (data.message.trim().length < 20)
    errors.message = "Message must be at least 20 characters";
  return errors;
};

const EMPTY_FORM: FormData = { firstName: "", lastName: "", email: "", subject: "", message: "" };

export interface ContactSupportMethod {
  /** Lucide registry key (resolved via `getVisualCmsIcon`). Defaults to Mail. */
  icon?: string | null;
  title: string;
  description: string;
  linkLabel?: string;
  linkHref?: string;
}

export interface ContactPageProps {
  heroEyebrow?: string;
  heroTitle?: string;
  heroTitleHighlight?: string;
  heroDescription?: string;
  heroImageUrl?: string;
  heroImageAlt?: string;
  heroLayoutVariant?: string;
  heroPrimaryCtaLabel?: string;
  heroPrimaryCtaHref?: string;
  heroSecondaryCtaLabel?: string;
  heroSecondaryCtaHref?: string;
  heroContainerStyle?: React.CSSProperties;

  /** "Get in Touch" sidebar — section heading + contact methods. */
  supportTitle?: string;
  supportMethods?: ContactSupportMethod[];

  /** Company Information card under the support sidebar. */
  companyTitle?: string;
  companyName?: string;
  companyDescription?: string;

  /** Inline style applied to the body <section> wrapping the support + form columns. */
  bodyContainerStyle?: React.CSSProperties;

  /** "Need Quick Answers?" closing CTA. */
  quickAnswersTitle?: string;
  quickAnswersDescription?: string;
  quickAnswersPrimaryCtaLabel?: string;
  quickAnswersPrimaryCtaHref?: string;
  quickAnswersSecondaryCtaLabel?: string;
  quickAnswersSecondaryCtaHref?: string;
  quickAnswersContainerStyle?: React.CSSProperties;
}

const DEFAULT_HERO_TITLE = 'Contact Us';
const DEFAULT_HERO_DESCRIPTION =
  "Have questions or need support? We're here to help! Get in touch with our friendly team.";

const DEFAULT_SUPPORT_TITLE = 'Get in Touch';
const DEFAULT_SUPPORT_METHODS: ContactSupportMethod[] = [
  {
    icon: 'Mail',
    title: 'Email Support',
    description:
      'Get help with your account, bookings, or listings. We typically respond within 24 hours.',
    linkLabel: 'hello@rentitforward.com.au',
    linkHref: 'mailto:hello@rentitforward.com.au',
  },
];

const DEFAULT_COMPANY_TITLE = 'Company Information';
const DEFAULT_COMPANY_NAME = 'Rent It Forward';
const DEFAULT_COMPANY_DESCRIPTION = 'ACN: 694 838 125';

const DEFAULT_QUICK_ANSWERS_TITLE = 'Need Quick Answers?';
const DEFAULT_QUICK_ANSWERS_DESCRIPTION =
  'Check our FAQ section for instant answers to common questions, or browse our help articles for detailed guides.';
const DEFAULT_QUICK_ANSWERS_PRIMARY_LABEL = 'View FAQ';
const DEFAULT_QUICK_ANSWERS_PRIMARY_HREF = '/faq';
const DEFAULT_QUICK_ANSWERS_SECONDARY_LABEL = 'How It Works';
const DEFAULT_QUICK_ANSWERS_SECONDARY_HREF = '/how-it-works';

const ContactIllustration: React.FC = () => (
  <div className="relative w-full max-w-[460px] aspect-[4/3]">
    {/* Soft halo */}
    <div className="absolute inset-x-4 inset-y-2 rounded-full bg-gradient-to-br from-v2-green-100/70 via-v2-green-50 to-transparent blur-2xl" />
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[72%] aspect-square rounded-full border border-dashed border-v2-green-200" />

    {/* Sparkle accents */}
    <Sparkles size={20} className="absolute top-6 left-12 text-v2-green-300 animate-pulse" />
    <Sparkles size={14} className="absolute top-20 right-14 text-v2-green-400" />
    <Sparkles size={16} className="absolute bottom-14 left-12 text-v2-green-300 animate-pulse" />

    {/* Center headset badge */}
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-gradient-to-br from-v2-green-500 to-v2-green-700 flex items-center justify-center text-white shadow-[0_18px_30px_-12px_rgba(30,140,58,0.5)]">
      <Headphones size={42} strokeWidth={1.8} />
    </div>

    {/* Speech bubble (top-right) */}
    <div className="absolute top-[10%] right-[8%] w-16 h-16 rounded-v2-lg bg-v2-neutral-0 border border-v2-green-100 shadow-[0_10px_24px_-14px_rgba(15,77,31,0.35)] flex items-center justify-center text-v2-green-600">
      <MessageSquare size={26} strokeWidth={1.8} />
    </div>

    {/* Message stub (bottom-left) */}
    <div className="absolute bottom-[12%] left-[8%] w-16 h-16 rounded-v2-lg bg-v2-neutral-0 border border-v2-green-100 shadow-[0_10px_24px_-14px_rgba(15,77,31,0.35)] flex items-center justify-center text-v2-green-600">
      <Mail size={24} strokeWidth={1.8} />
    </div>

    {/* Tiny shield badge (bottom-right) */}
    <div className="absolute bottom-[10%] right-[10%] w-14 h-14 rounded-full bg-gradient-to-br from-v2-green-400 to-v2-green-600 flex items-center justify-center text-white shadow-[0_8px_18px_-10px_rgba(15,77,31,0.55)]">
      <Check size={22} strokeWidth={2.4} />
    </div>
  </div>
);

/**
 * Splits a CMS-supplied description on the literal token "<br>" (or newlines)
 * so admins can opt-in to the same line break the static fallback ships with.
 */
function renderDescriptionWithBreaks(text: string): React.ReactNode {
  const parts = text.split(/<br\s*\/?>|\n/);
  if (parts.length <= 1) return text;
  return parts.map((part, i) =>
    i === parts.length - 1 ? (
      part
    ) : (
      <React.Fragment key={i}>
        {part}
        <br className="hidden sm:block" />
      </React.Fragment>
    )
  );
}

const Contact: React.FC<ContactPageProps> = ({
  heroEyebrow,
  heroTitle,
  heroTitleHighlight,
  heroDescription,
  heroImageUrl,
  heroImageAlt,
  heroLayoutVariant,
  heroPrimaryCtaLabel,
  heroPrimaryCtaHref,
  heroSecondaryCtaLabel,
  heroSecondaryCtaHref,
  heroContainerStyle,
  supportTitle,
  supportMethods,
  companyTitle,
  companyName,
  companyDescription,
  bodyContainerStyle,
  quickAnswersTitle,
  quickAnswersDescription,
  quickAnswersPrimaryCtaLabel,
  quickAnswersPrimaryCtaHref,
  quickAnswersSecondaryCtaLabel,
  quickAnswersSecondaryCtaHref,
  quickAnswersContainerStyle,
}) => {
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formStartTime, setFormStartTime] = useState<number>(() => Date.now());
  const recaptchaRef = useRef<RecaptchaFieldRef>(null);

  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";
  const isRecaptchaConfigured = recaptchaSiteKey.trim().length > 0;

  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  const handleCaptchaChange = (token: string | null) => {
    if (token) setSubmitError(null);
  };

  const title = heroTitle?.trim() || DEFAULT_HERO_TITLE;
  const description = heroDescription?.trim() || DEFAULT_HERO_DESCRIPTION;

  const supportHeading = supportTitle?.trim() || DEFAULT_SUPPORT_TITLE;
  const methods =
    supportMethods && supportMethods.length > 0 ? supportMethods : DEFAULT_SUPPORT_METHODS;
  const companyHeading = companyTitle?.trim() || DEFAULT_COMPANY_TITLE;
  const companyHeadline = companyName?.trim() || DEFAULT_COMPANY_NAME;
  const companyBody = companyDescription?.trim() || DEFAULT_COMPANY_DESCRIPTION;

  const quickAnswersHeading = quickAnswersTitle?.trim() || DEFAULT_QUICK_ANSWERS_TITLE;
  const quickAnswersBody = quickAnswersDescription?.trim() || DEFAULT_QUICK_ANSWERS_DESCRIPTION;
  const quickAnswersPrimaryLabel =
    quickAnswersPrimaryCtaLabel?.trim() || DEFAULT_QUICK_ANSWERS_PRIMARY_LABEL;
  const quickAnswersPrimaryHref =
    quickAnswersPrimaryCtaHref?.trim() || DEFAULT_QUICK_ANSWERS_PRIMARY_HREF;
  const quickAnswersSecondaryLabel =
    quickAnswersSecondaryCtaLabel?.trim() || DEFAULT_QUICK_ANSWERS_SECONDARY_LABEL;
  const quickAnswersSecondaryHref =
    quickAnswersSecondaryCtaHref?.trim() || DEFAULT_QUICK_ANSWERS_SECONDARY_HREF;


    console.log("methods",methods);
  const charCount = form.message.length;

  const handleChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    if (!isRecaptchaConfigured) {
      setSubmitError("reCAPTCHA is not configured. Please contact support.");
      return;
    }
    setLoading(true);
    // Invisible reCAPTCHA Enterprise: fetch a fresh token at submit time.
    const token = await recaptchaRef.current?.execute("contact");
    if (!token) {
      setLoading(false);
      setSubmitError("reCAPTCHA verification failed. Please try again.");
      return;
    }
    const result = await submitContactForm({ ...form, captchaToken: token, formStartTime });
    setLoading(false);
    if (result.success) {
      setSubmitted(true);
      recaptchaRef.current?.reset();
      setFormStartTime(Date.now());
    } else {
      if (result.errors) {
        const fieldErrors: FormErrors = {};
        Object.entries(result.errors).forEach(([field, message]) => {
          if (field in form) fieldErrors[field as keyof FormErrors] = message;
        });
        if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      }
      setSubmitError(result.message);
      recaptchaRef.current?.reset();
    }
  };

  return (
    <>
      <PageHeroLayout
        eyebrow={heroEyebrow?.trim() || "Contact"}
        eyebrowIcon={MessageSquare}
        title={title}
        titleHighlight={heroTitleHighlight?.trim() || undefined}
        description={description}
        primaryCta={
          heroPrimaryCtaLabel?.trim() && heroPrimaryCtaHref?.trim()
            ? { label: heroPrimaryCtaLabel.trim(), href: heroPrimaryCtaHref.trim() }
            : undefined
        }
        secondaryCta={
          heroSecondaryCtaLabel?.trim() && heroSecondaryCtaHref?.trim()
            ? { label: heroSecondaryCtaLabel.trim(), href: heroSecondaryCtaHref.trim() }
            : undefined
        }
        illustration={<ContactIllustration />}
        imageUrl={heroImageUrl}
        imageAlt={heroImageAlt}
        layoutVariant={heroLayoutVariant}
        containerStyle={heroContainerStyle}
      />

      {/* Main content */}
      <section className="bg-v2-neutral-0 py-24" style={bodyContainerStyle}>
        <Container>
        <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 items-start">
          {/* Left — contact info */}
          <div className="space-y-5">
            <h2 className="font-display text-[24px] font-bold text-v2-neutral-900">
              {supportHeading}
            </h2>

            {methods.map((method, index) => {
              const Icon = getVisualCmsIcon(method.icon ?? null) ?? Mail;
              return (
                <div key={`${method.title}-${index}`} className="flex gap-4">
                  <div className="w-11 h-11 rounded-v2-md bg-v2-green-50 flex items-center justify-center text-v2-green-500 shrink-0">
                    <Icon size={20} strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="font-semibold text-[15px] text-v2-neutral-900 mb-1">
                      {method.title}
                    </p>
                    <p className="text-[13px] text-v2-neutral-500 mb-2 leading-relaxed max-w-xs">
                      {method.description}
                    </p>
                    {method.linkHref && (method.linkLabel || method.linkHref) && (
                      <a
                        href={method.linkHref}
                        className="inline-flex items-center gap-1.5 text-v2-green-600 text-[13px] font-semibold hover:text-v2-green-700 transition-colors"
                      >
                        {method.linkLabel || method.linkHref}
                        <Send size={12} strokeWidth={2} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Company info card */}
            <div className="bg-v2-neutral-50 rounded-v2-lg p-5">
              <p className="font-semibold text-[14px] text-v2-neutral-900 mb-3">
                {companyHeading}
              </p>
              <p className="text-[13px] text-v2-neutral-500 leading-relaxed">
                <span className="text-v2-green-600 font-semibold">{companyHeadline}</span>
                {companyBody && (
                  <>
                    {" "}
                    <br />
                    {companyBody}
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div className="bg-v2-neutral-0 rounded-v2-xl border border-v2-neutral-200 p-7 lg:p-8">
            {submitted ? (
              <div className="flex flex-col items-center text-center py-8 gap-4">
                <div className="w-16 h-16 rounded-full bg-v2-green-100 flex items-center justify-center text-v2-green-500">
                  <Check size={28} strokeWidth={2.5} />
                </div>
                <h3 className="font-display text-[22px] font-bold text-v2-neutral-900">
                  Message Sent!
                </h3>
                <p className="text-[15px] text-v2-neutral-500 max-w-sm">
                  Thanks for reaching out. We&apos;ll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm(EMPTY_FORM);
                  }}
                  className="mt-2 px-5 py-2.5 rounded-v2-full border-[1.5px] border-v2-neutral-300 text-[14px] font-semibold text-v2-neutral-700 hover:bg-v2-neutral-50 transition-colors"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <>
                <h2 className="font-display text-[22px] font-bold text-v2-neutral-900 mb-6">
                  Send us a Message
                </h2>
                <form onSubmit={handleSubmit} noValidate className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      label="First Name"
                      placeholder="Your first name"
                      value={form.firstName}
                      onChange={handleChange("firstName")}
                      error={errors.firstName}
                      required
                    />
                    <Input
                      label="Last Name"
                      placeholder="Your last name"
                      value={form.lastName}
                      onChange={handleChange("lastName")}
                      error={errors.lastName}
                      required
                    />
                  </div>
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your.email@example.com"
                    value={form.email}
                    onChange={handleChange("email")}
                    error={errors.email}
                    required
                  />
                  <Select
                    label="Subject"
                    options={subjectOptions}
                    placeholder="Select a topic"
                    value={form.subject}
                    onChange={handleChange("subject")}
                    error={errors.subject}
                    required
                  />
                  <div>
                    <Textarea
                      label="Message"
                      placeholder="Tell us how we can help you..."
                      value={form.message}
                      onChange={handleChange("message")}
                      error={errors.message}
                      rows={5}
                      required
                    />
                    <p
                      className={`text-right text-[12px] mt-1 ${charCount > 1000 ? "text-v2-red-400" : "text-v2-neutral-400"}`}
                    >
                      {charCount}/1000 characters
                    </p>
                  </div>

                  <div className="flex justify-start">
                    {isRecaptchaConfigured ? (
                      <RecaptchaField ref={recaptchaRef} onVerify={handleCaptchaChange} />
                    ) : (
                      <p className="text-[13px] text-v2-red-400">
                        reCAPTCHA is not configured. Set NEXT_PUBLIC_RECAPTCHA_SITE_KEY.
                      </p>
                    )}
                  </div>

                  {submitError && (
                    <p
                      role="alert"
                      className="text-[13px] text-v2-red-400 bg-v2-red-50 border border-v2-red-100 rounded-v2-md px-3 py-2"
                    >
                      {submitError}
                    </p>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full justify-center"
                    loading={loading}
                    disabled={loading || !isRecaptchaConfigured}
                  >
                    Send Message
                  </Button>
                  <p className="text-center text-[12px] text-v2-neutral-400">
                    We&apos;ll get back to you within 24 hours.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
        </Container>
      </section>

      {/* Need Quick Answers section */}
      <section
        className="bg-v2-neutral-50 py-14 lg:py-16"
        style={quickAnswersContainerStyle}
      >
        <Container>
          <div className="text-center max-w-xl mx-auto">
            <h2 className="font-display text-[28px] sm:text-[32px] font-bold text-v2-neutral-900 mb-3">
              {quickAnswersHeading}
            </h2>
            <p className="text-[18px] text-v2-neutral-500 leading-relaxed mb-6">
              {renderDescriptionWithBreaks(quickAnswersBody)}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {quickAnswersPrimaryLabel && quickAnswersPrimaryHref && (
                <Link
                  href={quickAnswersPrimaryHref}
                  className="px-7 py-[14px] bg-v2-green-500 text-white text-[15px] font-semibold rounded-v2-full hover:bg-v2-green-600 transition-colors"
                >
                  {quickAnswersPrimaryLabel}
                </Link>
              )}
              {quickAnswersSecondaryLabel && quickAnswersSecondaryHref && (
                <Link
                  href={quickAnswersSecondaryHref}
                  className="px-[27px] py-[13px] bg-v2-neutral-0 border-[1.5px] border-v2-green-400 text-v2-green-500 text-[15px] font-semibold rounded-v2-full hover:bg-v2-green-50 transition-colors"
                >
                  {quickAnswersSecondaryLabel}
                </Link>
              )}
            </div>
          </div>
        </Container>
      </section>
    </>
  );
};

export default Contact;
